import type { FC } from 'react';
import React from 'react';
import { BlockDesktop as Block } from '@alfa-bank/site-block/desktop';
import { Grid } from '@alfa-bank/site-grid-universal';
import { TextDesktop as Text } from '@alfa-bank/site-text-v2/desktop';
import { LogoDesktop as Logo } from '@alfa-bank/site-invest-emitents-common/components/logo-desktop';
import { Hr } from '@alfa-bank/site-hr';
import {
    setFirstCharInUpperCase,
    getTickerOrIsin,
    getAssetNameByType,
} from '@alfa-bank/site-invest-emitents-common/helpers';
import cn from 'classnames';
import { useNamedContainers } from '@alfa-bank/site-named-container/utils';
import { ASSET_NAME_BOND } from '@alfa-bank/site-invest-emitents-common/src/constants';

import { Label } from '../../label/desktop';
import { PriceDesktop as Price } from '../../cells/price/desktop';
import { DynamicsDesktop as Dynamics } from '../../cells/dynamics/desktop';
import { SectorDesktop as Sector } from '../../cells/sector/desktop';
import { OpenAccountButton } from '../../open-account-button/desktop';
import type { TAssetHeaderDesktop } from '../models';

import styles from './styles.css';

export const AssetHeaderDesktop: FC<TAssetHeaderDesktop> = ({
    analytics,
    anchorLink,
    asset,
    buttonText,
    children,
    hideButton,
}) => {
    const { assetType, issuer } = asset;

    const tickerOrIsin = getTickerOrIsin(asset);
    const assetName = getAssetNameByType(assetType.code, asset);
    const { innerContent } = useNamedContainers(children);
    const isButtonVisible = !hideButton;

    return (
        <Block notPadded styler={{ paddingTop: 48, paddingBottom: 48 }}>
            <Block notPadded>
                <Grid
                    alignment="flexStart"
                    collapse
                    columnsWidth={[88, null]}
                    styler={{ marginBottom: 32 }}
                >
                    <Block notPadded styler={{ marginTop: 4 }}>
                        <Logo assetName={assetName} issuer={issuer} />
                    </Block>
                    <Block notPadded>
                        <Grid
                            colAutoWidth
                            columnsMargin={8}
                            justify="start"
                            styler={{ marginBottom: 8 }}
                        >
                            <Text
                                kind="secondary"
                                styler={{ margin: 0 }}
                                tag="span"
                                view="primary-small"
                            >
                                {setFirstCharInUpperCase(assetType.name)}
                            </Text>
                            <Label text={tickerOrIsin} />
                        </Grid>
                        <Text styler={{ margin: 0 }} tag="h1" view="large">
                            {assetName}
                        </Text>
                    </Block>
                </Grid>
                {assetType.code === ASSET_NAME_BOND ? (
                    <Grid
                        colAutoWidth
                        collapse
                        columnsMargin={32}
                        justify="start"
                        styler={{ marginBottom: 32 }}
                    >
                        <Dynamics asset={asset} />
                        <Price asset={asset} />
                        <Sector asset={asset} />
                    </Grid>
                ) : (
                    <Grid
                        colAutoWidth
                        collapse
                        columnsMargin={32}
                        justify="start"
                        styler={{ marginBottom: 32 }}
                    >
                        <Price asset={asset} />
                        <Dynamics asset={asset} />
                        <Sector asset={asset} />
                    </Grid>
                )}
            </Block>
            <Hr styler={{ marginBottom: 32 }} />
            {innerContent && (
                <Block dataTestId="inner-content-block" notPadded styler={{ marginBottom: 32 }}>
                    {innerContent}
                </Block>
            )}
            {isButtonVisible && (
                <Grid
                    className={cn(styles.external, styles.openAccountButtonGrid)}
                    collapse
                    columnsWidth={204}
                    justify="start"
                >
                    <OpenAccountButton
                        analytics={analytics}
                        anchorLink={anchorLink}
                        buttonText={buttonText}
                    />
                </Grid>
            )}
        </Block>
    );
};
